import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import { DatePicker } from '@material-ui/pickers'
import { Add, Delete, Event } from '@material-ui/icons'
import dayjs from 'dayjs'
import useAmenitiesApi from '../../hooks/amenities/useAmenitiesApi'
import { unavailabilityReasons } from '../../utils/amenityConstants'

// Each entry blocks a full local day [00:00, +24h). For multi-day spans the
// admin adds consecutive entries. The backend Postgres CHECK constraint
// enforces endDatetime > startDatetime; we never pass a zero-length window.
export default function AmenityUnavailabilityManager({ open, onClose, amenity }) {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')

  const [{ blackoutsState }, methods] = useAmenitiesApi()

  useEffect(() => {
    if (open && amenity?.id) {
      methods.getBlackouts(amenity.id)
    }
  }, [open, amenity?.id])

  const handleAdd = async () => {
    if (!selectedDate || !amenity?.id) return
    const reason = selectedReason === 'Otro' ? customReason.trim() : selectedReason
    if (!reason) return
    const start = dayjs(selectedDate).startOf('day')
    const end = start.add(1, 'day')
    try {
      await methods.createBlackout(amenity.id, {
        startDatetime: start.toISOString(),
        endDatetime: end.toISOString(),
        reason,
      })
      setSelectedDate(new Date())
      setSelectedReason('')
      setCustomReason('')
    } catch (e) {
      // Hook surfaces a toast.
    }
  }

  const handleRemove = async (blackoutId) => {
    if (!amenity?.id) return
    try {
      await methods.deleteBlackout(blackoutId, amenity.id)
    } catch (e) {
      // Hook surfaces a toast.
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Event />
          Gestionar Fechas No Disponibles - {amenity?.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Agregar fecha no disponible:
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <DatePicker
              label="Fecha no disponible"
              value={selectedDate}
              onChange={setSelectedDate}
              format="dd/MM/yyyy"
              minDate={new Date()}
              fullWidth
              inputVariant="outlined"
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel>Motivo</InputLabel>
              <Select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value)}
                label="Motivo"
              >
                {unavailabilityReasons.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    {reason}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedReason === 'Otro' && (
              <TextField
                fullWidth
                variant="outlined"
                label="Especificar motivo"
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Ingrese el motivo personalizado"
              />
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={
                !selectedDate ||
                !selectedReason ||
                (selectedReason === 'Otro' && !customReason.trim()) ||
                blackoutsState.isLoading
              }
            >
              Agregar Fecha
            </Button>
          </Box>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          Fechas no disponibles programadas:
        </Typography>
        <List>
          {(blackoutsState.blackouts || []).length > 0 ? (
            (blackoutsState.blackouts || []).map((b) => (
              <ListItem key={b.id} divider>
                <ListItemText
                  primary={dayjs(b.startDatetime).format('dddd D [de] MMMM [de] YYYY')}
                  secondary={`Motivo: ${b.reason || 'Sin motivo'}`}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    color="secondary"
                    onClick={() => handleRemove(b.id)}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary="No hay fechas programadas como no disponibles"
                secondary="Agregue fechas donde el amenity no estará disponible para reservas"
              />
            </ListItem>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
